// The photo index — reading the shared media collection (mediadoc.js) for a project, a task, a day or the whole
// org: filters, grouping (assessment → project → one group per task) and the one-line summary a link row shows.
// Pure; readers pass the `media` rows they already hold (useLocalCollection('media')).
import { MEDIA_SOURCE_LABEL } from './mediadoc.js';
import { PHOTO_TAG_LABEL, taskPhotoList } from './taskphotos.js';
import { localDateStr } from './time.js';

const TAG_ORDER = { before: 0, progress: 1, after: 2 };
const takenAt = (m) => Number(m?.takenAt) || Number(m?.at) || 0;

/** Oldest first by when it was taken (what timelines and the grid read). */
export const sortMedia = (media) => [...(media || [])].sort((a, b) => takenAt(a) - takenAt(b) || String(a.id).localeCompare(String(b.id)));
/** Newest first. */
export const sortMediaDesc = (media) => sortMedia(media).reverse();

export const mediaForProject = (media, projectId) => (projectId ? (media || []).filter((m) => m && m.projectId === projectId) : []);
export const mediaForTask = (media, taskId) => (taskId ? (media || []).filter((m) => m && m.taskId === taskId) : []);
export const mediaForEstimate = (media, estimateId) => (estimateId ? (media || []).filter((m) => m && m.estimateId === estimateId) : []);
export const photosOnly = (media) => (media || []).filter((m) => m && m.kind !== 'voice');

/**
 * A task's photos in the shape taskPhotoList gave ([{ uri, tag, at, addedAt, takenAt, caption, cover, id, thumb }]),
 * before → progress → after then oldest first — so the strips and the Daily Log read the index with no other change.
 */
export function taskPhotosFromMedia(media, taskId) {
  return mediaForTask(photosOnly(media), taskId)
    .map((m) => ({ id: m.id, uri: m.uri, thumb: m.thumbUri || m.uri, tag: m.tag || 'progress', at: takenAt(m), addedAt: Number(m.at) || 0, takenAt: Number(m.takenAt) || null, caption: m.caption || null, cover: !!m.cover }))
    .sort((a, b) => (TAG_ORDER[a.tag] ?? 1) - (TAG_ORDER[b.tag] ?? 1) || a.at - b.at);
}

/**
 * Narrow a list: { projectId, taskId, estimateId, tag, source, uid, date (local YYYY-MM-DD), from, to (ms), kind }.
 * Every given key must match; missing keys don't filter.
 */
export function filterMedia(media, f = {}) {
  return (media || []).filter((m) => {
    if (!m) return false;
    if (f.projectId && m.projectId !== f.projectId) return false;
    if (f.taskId && m.taskId !== f.taskId) return false;
    if (f.estimateId && m.estimateId !== f.estimateId) return false;
    if (f.tag && m.tag !== f.tag) return false;
    if (f.source && m.source !== f.source) return false;
    if (f.kind && (m.kind || 'photo') !== f.kind) return false;
    if (f.uid && (m.by || m.ownerUid) !== f.uid) return false;
    const t = takenAt(m);
    if (f.date && localDateStr(new Date(t)) !== f.date) return false;
    if (Number(f.from) > 0 && t < Number(f.from)) return false;
    if (Number(f.to) > 0 && t > Number(f.to)) return false;
    return true;
  });
}

/** "12 photos · 2 notes" / "1 photo" / "" */
export function mediaSummary(media) {
  const photos = photosOnly(media).length;
  const notes = (media || []).filter((m) => m && m.kind === 'voice').length;
  const parts = [];
  if (photos) parts.push(`${photos} photo${photos === 1 ? '' : 's'}`);
  if (notes) parts.push(`${notes} note${notes === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

/**
 * A project's media in groups, in the order the screen shows them:
 *   assessment (the bid's site photos, when the project came from one), project (shot at project level), then one
 *   group per task (tasks in the given order — the caller sorts them the way the project lists them), then any
 *   photo on a task the caller doesn't hold (deleted / out of scope) under "Other".
 * → [{ key, kind: 'assessment' | 'project' | 'task' | 'other', title, subtitle, taskId?, media: [...] }]
 */
export function groupMedia(media, { tasks = [], project = null, estimateNumber = null } = {}) {
  const sorted = sortMedia(media);
  const byTask = new Map();
  const assessment = [];
  const projectLevel = [];
  const other = [];
  const taskIds = new Set(tasks.map((t) => t.id));
  for (const m of sorted) {
    if (m.taskId) {
      if (!taskIds.has(m.taskId)) { other.push(m); continue; }
      if (!byTask.has(m.taskId)) byTask.set(m.taskId, []);
      byTask.get(m.taskId).push(m);
    } else if (m.source === 'assessment' || (m.estimateId && !m.projectId)) assessment.push(m);
    else projectLevel.push(m);
  }
  const groups = [];
  if (assessment.length) {
    const num = estimateNumber || project?.estimateNumber || null;
    groups.push({ key: 'assessment', kind: 'assessment', title: 'Assessment', subtitle: num ? `Bid ${num}` : 'Site photos from the bid', media: assessment });
  }
  if (projectLevel.length) groups.push({ key: 'project', kind: 'project', title: project?.name || 'Project', subtitle: 'Whole job', media: projectLevel });
  for (const t of tasks) {
    const list = byTask.get(t.id);
    if (list?.length) groups.push({ key: t.id, kind: 'task', taskId: t.id, title: t.title || 'Task', subtitle: t.status === 'done' ? 'Done' : t.status === 'void' ? 'Closed' : null, media: list });
  }
  if (other.length) groups.push({ key: 'other', kind: 'other', title: 'Other', subtitle: 'On tasks no longer here', media: other });
  return groups;
}

/** Media grouped by the local day it was taken, newest day first: [{ date, media }]. */
export function groupMediaByDay(media) {
  const days = new Map();
  for (const m of sortMediaDesc(media)) {
    const d = localDateStr(new Date(takenAt(m)));
    if (!days.has(d)) days.set(d, []);
    days.get(d).push(m);
  }
  return [...days.entries()].map(([date, list]) => ({ date, media: list }));
}

/** The one line under a photo: "Task · Framing · Before" / "Assessment · Bid C-26-10-004" / "Project". */
export function mediaLabel(m, { task = null, estimateNumber = null } = {}) {
  if (!m) return '';
  const parts = [MEDIA_SOURCE_LABEL[m.source] || 'Photo'];
  if (m.source === 'task' && task?.title) parts.push(task.title);
  if (m.source === 'assessment' && estimateNumber) parts.push(`Bid ${estimateNumber}`);
  if (m.tag && PHOTO_TAG_LABEL[m.tag]) parts.push(PHOTO_TAG_LABEL[m.tag]);
  if (m.kind === 'voice') parts.push('Voice note');
  return parts.join(' · ');
}

/**
 * A task's pictures from both homes while the task doc is still written beside the index (dual-write): the index's
 * docs for the task, plus any legacy picture on the task doc the index doesn't hold yet (matched by uri — the queue
 * swaps both to the same link). Shape = taskPhotoList's (+ id / thumb when indexed). Readers call this with the
 * `media` rows they hold; with none it is exactly taskPhotoList.
 */
export function photosOfTask(task, media = []) {
  const legacy = taskPhotoList(task);
  if (!task) return legacy;
  const rows = media instanceof Map ? media.get(task.id) : media;   // a mediaByTask() map: this task's rows, found once
  if (!rows?.length) return legacy;
  const indexed = taskPhotosFromMedia(rows, task.id);
  if (!indexed.length) return legacy;
  const have = new Set(indexed.map((p) => p.uri));
  return [...indexed, ...legacy.filter((p) => !have.has(p.uri))].sort((a, b) => (TAG_ORDER[a.tag] ?? 1) - (TAG_ORDER[b.tag] ?? 1) || a.at - b.at);
}

/**
 * The index keyed by task — build it once, then photosOfTask(task, map) reads each task's rows without filtering the
 * whole collection per task (the field dashboard walks every task). → Map<taskId, media[]>
 */
export function mediaByTask(media) {
  const m = new Map();
  for (const row of media || []) {
    if (!row?.taskId) continue;
    if (!m.has(row.taskId)) m.set(row.taskId, []);
    m.get(row.taskId).push(row);
  }
  return m;
}

/** Pictures with no task (project-level, assessment) taken on a local day, optionally within some projects. */
export function mediaForDay(media, date, { projectIds = null } = {}) {
  return sortMedia(filterMedia(photosOnly(media), { date })).filter((m) => !m.taskId && (!projectIds || projectIds.has?.(m.projectId) || (Array.isArray(projectIds) && projectIds.includes(m.projectId))));
}
